import moment from 'moment'
import React from 'react'
import { IfOverflowType, ReferenceArea } from 'recharts'
import type { InfluxSeriesMeasuresDatum, InfluxSeriesValue, InfluxSeriesMeasuresData } from './influx/_types'

interface MeasureValueTimeRangeSegmentWithValue { value: InfluxSeriesValue, start: number, end: number }

/**
 * Filters measures data by a condition on a specified measure field,
 * emitting a new array that includes the endpoints of every interval where the condition is met.
 * Ensures no duplicate points are included when intervals are consecutive.
 *
 * @template K - Keys of the data records (measure slugs).
 * @param {MeasuresData<K>} measuresData - Array of input measurement records.
 * @param {K} conditionMeasureSlug - Name of the field to check the condition on.
 * @param {(value: number) => boolean} condition - Callback returning true for values that pass.
 * @returns {MeasuresData<K>} Array including the endpoints of each matching interval (no duplicated times).
 *
 * @example
 * // Input data with two value fields:
 * const data = [
 *   { time: 1000, a: 12, b: 4 },
 *   { time: 2000, a: 17, b: 0 },
 *   { time: 3000, a: 9,  b: 2 },
 *   { time: 4000, a: 15, b: 8 },
 *   { time: 5000, a: 7,  b: 10 },
 * ];
 * // Get intervals where 'a' > 10:
 * getMeasuresValuesByCondition(data, 'a', v => v > 10)
 * // Output:
 * // [
 * //   { time: 1000, a: 12, b: 4 },
 * //   { time: 2000, a: 17, b: 0 },
 * //   { time: 4000, a: 15, b: 8 },
 * //   { time: 5000, a: 7, b: 10 },
 * // ]
 */
const getMeasuresValuesByCondition = <K extends string, C extends K>(
  measuresData: InfluxSeriesMeasuresData<K>,
  conditionMeasureSlug: C,
  condition: (value: number) => boolean
): InfluxSeriesMeasuresData<K> => {
  const out: InfluxSeriesMeasuresData<K> = []
  const n = measuresData.length
  if (n < 2) { return out }

  const passes = (v: number | undefined | null): v is number => v != null && condition(v)

  let lastPushedTime: number | null = null
  const push = (point: InfluxSeriesMeasuresDatum<K>) => {
    if (lastPushedTime === point.time) { return }
    out.push(point)
    lastPushedTime = point.time
  }

  for (let i = 0; i < n - 1; i++) {
    const value = measuresData[i][conditionMeasureSlug]
    if (!passes(value)) { continue }
    push(measuresData[i])
    push(measuresData[i + 1])
  }

  return out
}

/**
 * Splits measure data into time segments where the measure satisfies the given condition.
 *
 * @param measuresData - The array of data points containing time and values for different measures.
 * @param measureSlug - The key to access the value for the specific measure.
 * @param condition - A predicate function to filter values. Only values for which this condition returns true will generate a segment.
 * @returns An array of segments; each segment has a start time, end time, and value.
 *
 * Segments are created per time interval: for each point `i` (except the last),
 * we create a segment `[time[i], time[i+1]]` when the value at `i` is not null/undefined
 * and satisfies the condition. The value can vary across consecutive segments.
 *
 * @example
 *   const data = [
 *     { time: 1705320000000, engineSpeedRpm: 800 },
 *     { time: 1705320060000, engineSpeedRpm: 1100 },
 *     { time: 1705320120000, engineSpeedRpm: 1450 },
 *     { time: 1705320180000, engineSpeedRpm: null }, // missing sample
 *     { time: 1705320240000, engineSpeedRpm: 900 },
 *     { time: 1705320300000, engineSpeedRpm: 1250 },
 *     { time: 1705320360000, engineSpeedRpm: 700 },
 *   ]
 *   getMeasureValueSegmentsByCondition(data, 'engineSpeedRpm', rpm => rpm >= 1000)
 *   // Returns:
 *   // [
 *   //   { start: 1705320060000, end: 1705320120000, value: 1100 },
 *   //   { start: 1705320120000, end: 1705320180000, value: 1450 },
 *   //   { start: 1705320300000, end: 1705320360000, value: 1250 },
 *   // ]
 */
const getMeasureValueSegmentsByCondition = <K extends string>(
  measuresData: InfluxSeriesMeasuresData<K>,
  measureSlug: K,
  condition: (value: number) => boolean
): MeasureValueTimeRangeSegmentWithValue[] => {
  const out: MeasureValueTimeRangeSegmentWithValue[] = []
  const n = measuresData.length
  if (n < 2) { return out }

  const passes = (v: number | undefined | null): v is number => v != null && condition(v)

  // Use a pointer to track the segment we are currently building
  let currentSegment: MeasureValueTimeRangeSegmentWithValue | null = null

  for (let i = 0; i < n - 1; i++) {
    const value = measuresData[i][measureSlug]
    const nextTime = measuresData[i + 1].time

    if (passes(value)) {
      if (currentSegment) {
        currentSegment.end = nextTime
      } else {
        currentSegment = {
          start: measuresData[i].time,
          end: nextTime,
          value,
        }
      }
    } else if (currentSegment) {
      out.push(currentSegment)
      currentSegment = null
    }
  }

  if (currentSegment) {
    out.push(currentSegment)
  }

  return out
}

/**
 * Builds time segments by tracking when a value changes (including null/undefined values).
 *
 * A segment is emitted for every value (including null/undefined) and spans consecutive samples where
 * the value remains the same. Null/undefined values are represented with a null-valued segment.
 *
 * Segments with zero duration are not emitted.
 *
 * @example
 *   const data = [
 *     { time: 1705320000000, state: 1 },
 *     { time: 1705320060000, state: 1 },
 *     { time: 1705320120000, state: null }, // becomes a null-valued segment
 *     { time: 1705320180000, state: 1 },
 *     { time: 1705320240000, state: 2 },    // boundary: value changed to a defined value
 *     { time: 1705320300000, state: 2 },
 *   ]
 *   getMeasureValueSegments(data, 'state')
 *   // Returns:
 *   // [
 *   //   { start: 1705320000000, end: 1705320120000, value: 1 },
 *   //   { start: 1705320120000, end: 1705320180000, value: null },
 *   //   { start: 1705320180000, end: 1705320240000, value: 1 },
 *   //   { start: 1705320240000, end: 1705320300000, value: 2 },
 *   // ]
 */
const getMeasureValueSegments = <K extends string>(dataSrc: InfluxSeriesMeasuresData<K>, key: K): MeasureValueTimeRangeSegmentWithValue[] => {
  const out: MeasureValueTimeRangeSegmentWithValue[] = []

  const n = dataSrc.length
  if (n < 2) { return out }

  let segmentStart: number | null = null
  let segmentValue: number | null = null

  const flush = (end: number) => {
    if (segmentStart == null) { return }
    if (end <= segmentStart) { return }
    out.push({ start: segmentStart, end, value: segmentValue })
  }

  for (let i = 0; i < n - 1; i++) {
    const current = dataSrc[i]
    const rawValue = current[key]
    const currentValue = typeof rawValue === 'number' ? rawValue : null

    if (segmentStart == null) {
      segmentStart = current.time
      segmentValue = currentValue
      continue
    }

    if (segmentValue !== currentValue) {
      flush(current.time)
      segmentStart = current.time
      segmentValue = currentValue
    }
  }

  flush(dataSrc[n - 1].time)
  return out
}

/**
 * Joins consecutive area sections that are directly adjacent in time, merging them into a single section.
 *
 * This function takes a list of AreaSection objects and merges those where the end time (`x2`) of one section
 * is equal to the start time (`x1`) of the next section. The merged section will have the start time (`x1`)
 * of the first section and the end time (`x2`) of the last merged section.
 *
 * @param {AreaSection[]} list - The array of area sections to join.
 * @returns {AreaSection[]} A new array of area sections with consecutive sections merged.
 *
 * Example:
 * Input: [
 *   { x1: 0, x2: 10, value: 1 },
 *   { x1: 10, x2: 20, value: 1 },
 *   { x1: 25, x2: 30, value: 0 }
 * ]
 * Output: [
 *   { x1: 0, x2: 20, value: 1 },
 *   { x1: 25, x2: 30, value: 0 }
 * ]
 */
const joinSubsequentMeasureValueSegments = (segments: MeasureValueTimeRangeSegmentWithValue[]): MeasureValueTimeRangeSegmentWithValue[] => {
  const joinedAreas: MeasureValueTimeRangeSegmentWithValue[] = []
  for (let i = 0; i < segments.length; i++) {
    const current: MeasureValueTimeRangeSegmentWithValue = { ...segments[i] }
    while (current.end === segments[i + 1].start && i < segments.length - 1) {
      current.end = segments[i + 1].end
      i++
    }
    joinedAreas.push(current)
  }
  return joinedAreas
}

interface ITimeRangeColoredAreaOptions {
  opacity?: number
  overflow?: IfOverflowType
  xAxisId?: string | number
  yAxisId?: string | number
}

interface TimeRangeColoredSegment { start: number, end: number, color: string }   

/**
 * Helper function to generate Recharts ReferenceArea elements highlighting ranges on a time chart.
 * 
 * @param {Object} props - The properties for the area generator.
 * @param {TimeRangeColoredSegment[]} props.segments - Array of colored time range segments to render.
 * @param {ITimeRangeColoredAreaOptions} [props.options] - Options for appearance and axes assignment.
 * @returns {JSX.Element[]} - Array of ReferenceArea elements corresponding to the segments.
 * 
 * Each segment is rendered as a colored rectangular area using Recharts ReferenceArea,
 * useful for highlighting warning/alert regions directly on a line chart.
 * 
 * Example usage:
 * 
 *   generateTimeRangeColoredArea({
 *     segments: [
 *       { start: 1600000000, end: 1600000300, color: '#ff0000' }
 *     ],
 *     options: { opacity: 0.5, yAxisId: '1' }
 *   })
 */
function generateTimeRangeColoredArea(props: {segments: TimeRangeColoredSegment[], options?: ITimeRangeColoredAreaOptions}): JSX.Element[] {
  const { segments, options } = props
  const opacity = options?.opacity ?? 1
  const overflow = options?.overflow ?? 'visible'

  return segments.map(({ start, end, color }: TimeRangeColoredSegment) => {
    return (
      <ReferenceArea
        ifOverflow={overflow}
        key={`${start}-${end}-${color}`}
        x1={start}
        x2={end}
        stroke={color}
        fill={color}
        strokeWidth={1}
        strokeLinejoin="miter"
        fillOpacity={opacity}
        strokeOpacity={opacity}
        yAxisId={options?.yAxisId}
        xAxisId={options?.xAxisId}
      />
    )
  })
}

/**
 * Parses a duration string and returns the total seconds.
 * @param {string} durationString - The duration string formatted as "XXh YYm".
 * @returns {number} - The total seconds.
 */
function parseDuration(durationString: string): moment.Duration {
  // Regular expression to extract hours and minutes
  const regex = /(\d{1,2})h (\d{1,2})m/
  const match = durationString.match(regex)

  if (match) {
    const hours = parseInt(match[1], 10)
    const minutes = parseInt(match[2], 10)

    return moment.duration({ hours, minutes })
  }
  throw new Error('Invalid duration format')
}

export {
  getMeasureValueSegmentsByCondition,
  getMeasureValueSegments,
  parseDuration,
  joinSubsequentMeasureValueSegments,
  generateTimeRangeColoredArea,
  getMeasuresValuesByCondition,
}
export type { InfluxSeriesMeasuresDatum, InfluxSeriesMeasuresData, MeasureValueTimeRangeSegmentWithValue, TimeRangeColoredSegment }
